import { concat, match } from 'ramda'

import { sidecarDomains } from '../environments'

/**
 * This pulls the sidecar domain from whatever our host is, then
 * it appends the specified subDomain to create an API host or URL
 * to use within any of our apps, in any environment. This is
 * curried so that we can create a function that only needs to be
 * supplied the location host.
 *
 * Given: 'dev-api' and 'sidecarhealth.com'
 * Returns: 'https://dev-api.sidecarhealth.com'
 *
 * See see tests and uses in endpoints.js for more examples
 * @param {string} subDomain The subdomain to include (ex: dev-api)
 * @param {string} host The host to use (ex: sidecarhealth.com)
 */
export const buildAppHost = subDomain => host => {
  let prefix = `https://${subDomain ? subDomain + '.' : ''}`

  // if we're doing things locally, we should just use sidecarhealth
  // and not worry about any other domain.
  if (/(localhost|ngrok)/.test(host)) return `${prefix}sidecarhealth.com`

  // but if the subDomain is for prod, we need to strip out the "prod-"
  // because prod domains should be minimal and clean. The exception is
  // for our backend prod APIs because they still require the "prod-".
  if (subDomain && /^prod/.test(subDomain) && !/api$/.test(subDomain)) {
    // we only want to strip out the "prod-" portion, but leave
    // behind the 'subsub', like "app", "admin", "tooltips" or whatever
    const subsub = subDomain.split('-')[1]
    prefix = subsub === 'site' ? 'https://' : `https://${subsub}.`
  }

  // create a regex to target the domain from the host. We use a
  // loop to create the regex to make sure that we include all the
  // domains that we support.
  const regex = new RegExp(
    `(${sidecarDomains.map(d => d.replace('.', '\\.')).join('|')})$`,
  )
  let scHost = match(regex, host)[0]
  if (/api$/.test(subDomain)) {
    scHost = 'sidecarhealth.com'
  }

  // combine the prefix with our matched domain. We use match because
  // it will always return our domain as [0]. Using host.split('.') is
  // less accurate/efficient depending if there is a subdomain or not.
  return concat(prefix, scHost)
}
